
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { OwnerStatementGeneratePayload } from '../../services/api/owners';
import { useToast } from '../../components/common/ToastContext';
import { paymentSchema, camDataSchema } from '@repo/shared-types/accounting/schemas';
import { tenantFinancialSummarySchema } from '@repo/shared-types/accounting/tenantFinancialSummary';
import { fileRecordResponseSchema } from '@repo/shared-types/files/schemas';
import { ownerStatementSchema } from '@repo/shared-types/accounting/statementSchemas';
import { paymentMethodSchema } from '@repo/shared-types/settings/schemas';
import { tenantSettingsSchema, integrationSettingsSchema } from '@repo/shared-types/settings/schemas';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useTenantPayments = (tenantId: string) => {
    return useQuery({
        queryKey: ['portal', 'payments', tenantId],
        queryFn: async () => {
            const data = await api.portal.payments.list(tenantId);
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(paymentSchema, data);
            if (!validation.valid) {
                logger.error('Invalid payments query response:', validation.errors);
                throw new Error('Invalid payments data received from API');
            }
            return validation.data! as import('types').Payment[];
        },
        enabled: !!tenantId
    });
};

export const useTenantWalletFinancialSummary = (tenantId: string) => {
    return useQuery({
        queryKey: ['portal', 'wallet', tenantId],
        queryFn: async () => {
            const data = await api.portal.wallet.financialSummary(tenantId);
            const validation = validateEntity(tenantFinancialSummarySchema, data);
            if (!validation.valid) {
                logger.error('Invalid tenant wallet summary response:', validation.errors);
                throw new Error('Invalid wallet summary data received from API');
            }
            return validation.data!;
        },
        enabled: !!tenantId,
    });
};

/**
 * Refetches every `useTenantWalletFinancialSummary` query (prefix match).
 * Use after: POST /accounting/tenants/:id/fund succeeds, Stripe `confirmPayment` resolves succeeded,
 * or any step where ledger credit may have changed (webhook is server-side; client refresh depends on this).
 */
export function invalidatePortalTenantWalletQueries(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: ['portal', 'wallet'] });
}

export type TenantFundAccountVariables = {
    tenantId: string;
    amountCents: number;
    paymentMethodId?: string;
    idempotencyKey?: string;
};

/** Creates Stripe PI for tenant wallet top-up; on success invalidates wallet + portal payments for fresh balances. */
export const useTenantFundAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ tenantId, ...body }: TenantFundAccountVariables) =>
            api.accounting.tenants.fund(tenantId, body),
        onSuccess: (_data, variables) => {
            invalidatePortalTenantWalletQueries(queryClient);
            void queryClient.invalidateQueries({ queryKey: ['portal', 'payments', variables.tenantId] });
            void queryClient.invalidateQueries({ queryKey: ['portal', 'payments'] });
            void queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] });
        },
    });
};

export const useTenantDocuments = (tenantId: string) => {
    return useQuery({
        queryKey: ['portal', 'documents', tenantId],
        queryFn: async () => {
            const data = await api.portal.documents.list(tenantId);
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(fileRecordResponseSchema, data);
            if (!validation.valid) {
                logger.error('Invalid tenant documents query response:', validation.errors);
                throw new Error('Invalid tenant documents data received from API');
            }
            return validation.data! as import('types').TenantDocument[];
        },
        enabled: !!tenantId
    });
};

export const useUploadDocument = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (doc: { tenantId: string; name: string; type: string; url: string; uploadedAt: string; checklistItemId?: string }) => api.portal.documents.create(doc),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['portal', 'documents', data.tenantId] });
            if (variables.checklistItemId) {
                queryClient.invalidateQueries({ queryKey: ['onboarding'] });
            }
            showToast('Document uploaded successfully', 'success');
        }
    });
};

export const useAnnouncements = () => {
    return useQuery({
        queryKey: ['portal', 'announcements'],
        queryFn: () => api.portal.announcements.list(),
    });
};

export const useCreateAnnouncement = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (announcement: { id: string; title: string; content: string; date: string; author: string; role: string; avatar: string; likes: number; comments: number }) =>
            api.portal.announcements.create(announcement),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['portal', 'announcements'] });
            showToast('Announcement created successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create announcement', 'error');
        }
    });
};

export const useUpdateAnnouncement = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<{ id: string; title: string; content: string; date: string; author: string; role: string; avatar: string; likes: number; comments: number }> }) =>
            api.portal.announcements.update(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['portal', 'announcements'] });
            showToast('Announcement updated successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update announcement', 'error');
        }
    });
};

export const useDeleteAnnouncement = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.portal.announcements.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['portal', 'announcements'] });
            showToast('Announcement deleted successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete announcement', 'error');
        }
    });
};

export const useOwnerStatements = () => {
    return useQuery({
        queryKey: ['owners', 'statements'],
        queryFn: async () => {
            const data = await api.owners.statements.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(ownerStatementSchema, data);
            if (!validation.valid) {
                logger.error('Invalid owner statements query response:', validation.errors);
                throw new Error('Invalid owner statements data received from API');
            }
            return validation.data! as import('types').OwnerStatement[];
        },
    });
};

export const useGenerateStatement = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ ownerId, period, startDate, endDate }: { ownerId: string; period: string; startDate?: string; endDate?: string }) =>
            api.owners.statements.generate(ownerId, period, startDate, endDate),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['owners', 'statements'] });
            showToast('Statement generated successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to generate statement', 'error');
        }
    });
};

export const useCreateStatement = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation<
        import('@repo/shared-types').OwnerStatement,
        Error,
        OwnerStatementGeneratePayload
    >({
        mutationFn: (payload) => api.owners.statements.create(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['owners', 'statements'] });
            showToast('Statement created successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create statement', 'error');
        }
    });
};

export const useCAMData = () => {
    return useQuery({
        queryKey: ['accounting', 'cam'],
        queryFn: async () => {
            const data = await api.accounting.cam.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(camDataSchema, data);
            if (!validation.valid) {
                logger.error('Invalid CAM data query response:', validation.errors);
                throw new Error('Invalid CAM data received from API');
            }
            return validation.data! as import('types').CAMData[];
        },
    });
};

// Payment Methods
export const usePaymentMethods = (tenantId?: string) => {
    return useQuery({
        queryKey: ['paymentMethods', tenantId],
        queryFn: async () => {
            const data = await api.paymentMethods.list(tenantId);
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(paymentMethodSchema, data);
            if (!validation.valid) {
                logger.error('Invalid payment methods query response:', validation.errors);
                throw new Error('Invalid payment methods data received from API');
            }
            // Cast to PaymentMethod type from payments/paymentMethods (required fields)
            return validation.data! as import('@repo/shared-types/payments/paymentMethods').PaymentMethod[];
        },
        enabled: !!tenantId
    });
};

export const useCreatePaymentMethod = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (method: Parameters<typeof api.paymentMethods.create>[0]) =>
            api.paymentMethods.create(method),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods', data.tenantId] });
            showToast('Payment method added successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to add payment method', 'error');
        }
    });
};

export const useUpdatePaymentMethod = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.paymentMethods.update>[1] }) =>
            api.paymentMethods.update(id, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods', data.tenantId] });
            showToast('Payment method updated successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to update payment method', 'error');
        }
    });
};

export const useDeletePaymentMethod = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.paymentMethods.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
            showToast('Payment method deleted successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete payment method', 'error');
        }
    });
};

export const useRefundPayment = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ paymentId, amount, reason }: { paymentId: string; amount?: number; reason?: string }) =>
            api.portal.payments.refund(paymentId, amount, reason),
        onSuccess: (_, _variables) => {
            queryClient.invalidateQueries({ queryKey: ['portal', 'payments'] });
            invalidatePortalTenantWalletQueries(queryClient);
            queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] });
            showToast('Payment refunded successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to refund payment', 'error');
        }
    });
};

// Owner Settings
export const useOwnerSettings = (ownerId?: string) => {
    return useQuery({
        queryKey: ['ownerSettings', ownerId],
        queryFn: () => api.ownerSettings.get(ownerId!),
        enabled: !!ownerId
    });
};

export const useUpdateOwnerSettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ ownerId, updates }: { ownerId: string; updates: Parameters<typeof api.ownerSettings.update>[1] }) =>
            api.ownerSettings.update(ownerId, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['ownerSettings', data.ownerId] });
            showToast('Settings saved successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to save settings', 'error');
        }
    });
};

// Tenant Settings
export const useTenantSettings = (tenantId: string) => {
    return useQuery({
        queryKey: ['tenantSettings', tenantId],
        queryFn: async () => {
            const data = await api.tenantSettings.get(tenantId);
            // ✅ Validate query response with Zod schema
            if (data) {
                const validation = validateEntity(tenantSettingsSchema, data);
                if (!validation.valid) {
                    logger.error('Invalid tenant settings query response:', validation.errors);
                    throw new Error('Invalid tenant settings data received from API');
                }
                return validation.data! as import('@repo/shared-types/settings/tenantSettings').TenantSettings;
            }
            return null;
        },
        enabled: !!tenantId
    });
};

export const useUpdateTenantSettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ tenantId, updates }: { tenantId: string; updates: Parameters<typeof api.tenantSettings.update>[1] }) =>
            api.tenantSettings.update(tenantId, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['tenantSettings', data.tenantId] });
            showToast('Settings saved successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to save settings', 'error');
        }
    });
};

// Vendor Settings
export const useVendorSettings = (vendorId?: string) => {
    return useQuery({
        queryKey: ['vendorSettings', vendorId],
        queryFn: () => api.vendorSettings.get(vendorId!),
        enabled: !!vendorId
    });
};

export const useUpdateVendorSettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ vendorId, updates }: { vendorId: string; updates: Parameters<typeof api.vendorSettings.update>[1] }) =>
            api.vendorSettings.update(vendorId, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['vendorSettings', data.vendorId] });
            showToast('Settings saved successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to save settings', 'error');
        }
    });
};

// Investor Settings
export const useInvestorSettings = (investorId?: string) => {
    return useQuery({
        queryKey: ['investorSettings', investorId],
        queryFn: () => api.investorSettings.get(investorId!),
        enabled: !!investorId
    });
};

export const useUpdateInvestorSettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ investorId, updates }: { investorId: string; updates: Parameters<typeof api.investorSettings.update>[1] }) =>
            api.investorSettings.update(investorId, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['investorSettings', data.investorId] });
            showToast('Settings saved successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to save settings', 'error');
        }
    });
};

export const useIntegrationSettingsByType = (integrationType: string) => {
    return useQuery({
        queryKey: ['integrationSettings', 'byType', integrationType],
        queryFn: async () => {
            const data = await api.integrationSettings.getByType(integrationType);
            // ✅ Validate query response with Zod schema
            if (data) {
                const validation = validateEntity(integrationSettingsSchema, data);
                if (!validation.valid) {
                    logger.error('Invalid integration settings query response:', validation.errors);
                    throw new Error('Invalid integration settings data received from API');
                }
                return validation.data! as import('@repo/shared-types/settings/integrationSettings').IntegrationSettings;
            }
            return null;
        },
        enabled: !!integrationType,
    });
};

export const useDownloadFile = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (fileId: string) => api.files.download(fileId),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['files'] });
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            // The API returns file info, but actual download is handled by browser
            showToast('File download initiated', 'success');
            return result;
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to download file', 'error');
        }
    });
};


